// src/services/eventService.js
// 78차 62차: getActivePickStoreEvents의 PREMIUM 게이트를 store 전체가 아니라 이 PICK이
// 속한 지점(branchIdx)의 plan 기준으로 판정하도록 수정(plan이 지점별로 다를 수 있음).
// 웹 EventPage.jsx 기준: isActive, startDate, endDate, type, badgeColor, title, description
// 71차: 매장 브랜드명 폴백을 productService.getStoreDisplayName()으로 통일
// 75차: 데드코드 정리 — getActiveEvents/getEndedEvents/getEventById/seedWelcomeEvent/
// TYPE_LABEL 삭제(전부 호출부 없음). notices/events 컬렉션 분리 이후 공지·소식은
// notices 컬렉션을 EventsScreen.js가 직접 조회하고, 이 파일은 이제 PICK 테마
// (getActivePickStoreEvents)만 담당한다.
// 78차: getAllEvents 외부 호출부 없음 확인 — 같은 파일의 getActivePickStoreEvents가
// 내부적으로만 쓰고 있어 export 제거(비공개 함수로 낮춤).
// 78차 14차: BOOST 플랜 폐지, PICK 기능이 PREMIUM으로 흡수됨에 따라
// getActiveBoostStoreEvents → getActivePickStoreEvents로 개명 + 게이트 조건 변경.
import {
  collection, getDocs,
  query, orderBy, where,
} from 'firebase/firestore';
import { db } from './firebase';
import { planAtLeast } from '../constants';
import { getStoreDisplayName, parseProductId } from './productService';
import { getBranchPlan } from './feeService';
import { todayStr } from '../utils/dateUtils';

const COL = 'events';
// 78차: UTC 기준(toISOString)이던 것을 로컬 기준으로 교체 — PICK 테마가 한국 새벽 0~9시엔
// 실제 노출 종료일보다 하루 일찍 꺼지거나, 시작일 당일 새벽엔 아직 안 켜져 보이던 버그.
const today = () => todayStr();

// ─── 전체 이벤트 (웹 getAllEvents와 동일) ────────────────────
const getAllEvents = async () => {
  const snap = await getDocs(
    query(collection(db, COL), orderBy('createdAt', 'desc'))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// 68차: isActive 필드 의존 버그 수정
// StoreAdminScreen.js의 EventsTab(매장관리자 이벤트 등록)은 isActive 필드를
// 저장하지 않는다 — 즉 매장이 등록한 이벤트는 e.isActive가 undefined라서
// 기존 `e.isActive && ...` 필터에 항상 걸려 제외되고 있었다(고객에게 노출된 적 없음).
// 활성 여부는 원칙적으로 startDate~endDate 기간으로 판단하고,
// isActive가 명시적으로 false인 경우에만 강제 비활성(관리자 수동 중단용)으로 취급한다.
const isWithinPeriod = (e, t) => !!e.startDate && !!e.endDate && e.startDate <= t && e.endDate >= t;
const isForcedInactive = (e) => e.isActive === false;

// ─── 68차 신규: PREMIUM 플랜 매장의 활성 이벤트 (고객용 이벤트 탭 노출용) ──
// (78차 14차: BOOST 폐지로 PICK이 PREMIUM에 흡수되면서 게이트가 boost→premium으로 변경)
// stores 컬렉션에서 plan이 premium 이상인 매장만 골라, 그 매장이 등록한 활성 이벤트를
// 매장명과 함께 반환한다. (PREMIUM 플랜의 "PICK 노출" 셀링포인트를 실제로 구현)
export const getActivePickStoreEvents = async () => {
  const t = today();
  try {
    // 80차 후속: [버그 수정] stores 전체조회와 events 전체조회가 서로 독립적인데
    // 순차(await→await)로 실행돼 두 번의 Firestore 왕복시간이 그대로 더해지고
    // 있었음 — 스플래시 PICK 광고가 이 함수 응답을 기다리는데 이 지연이 그대로
    // "광고가 늦게 뜬다"로 이어짐(직접 curl로 재현). Promise.all로 병렬화해
    // 둘 중 느린 쪽 시간만 걸리도록 단축.
    const [storeSnap, allEvents] = await Promise.all([
      getDocs(collection(db, 'stores')),
      getAllEvents(),
    ]);
    // 78차 62차: plan이 지점(branches[i])별로 다를 수 있어, 매장 단위가 아니라
    // 이 PICK이 속한 지점의 plan으로 판정해야 함 — 우선 삭제 안 된 매장만 후보로
    // 추리고, plan 게이트는 아래에서 이벤트별 productId의 branchIdx로 판단.
    const activeStores = storeSnap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((s) => s.isDeleted !== true);

    if (activeStores.length === 0) return [];
    const storeMap = new Map(activeStores.map((s) => [s.id, s]));

    return allEvents
      .filter((e) => e.storeId && storeMap.has(e.storeId))
      .filter((e) => !isForcedInactive(e) && isWithinPeriod(e, t))
      .filter((e) => {
        const store = storeMap.get(e.storeId);
        const parsed = e.productId ? parseProductId(e.productId) : null;
        return planAtLeast(getBranchPlan(store, parsed?.branchIdx ?? null), 'premium');
      })
      // 79차: [버그 수정] PICK 등록 후 매장관리자가 그 지점/테마를 소프트삭제해도
      // 이 카드는 계속 노출되고 있었음(썸네일/가격까지 그대로) — 탭하면 ThemeDetail이
      // getProductById로 null을 받아 이름/이미지 없는 빈 화면이 됨(리뷰/공지 화면
      // 재정독 fork로 발견). productId가 가리키는 지점/테마가 삭제됐으면 제외
      // (productId 파싱 자체가 안 되는 레거시 이벤트는 배제 대상에서 제외해 안전하게 둠).
      .filter((e) => {
        const parsed = e.productId ? parseProductId(e.productId) : null;
        if (!parsed) return true;
        const store  = storeMap.get(e.storeId);
        const branch = (store.branches || [])[parsed.branchIdx];
        if (!branch || branch.isDeleted === true) return false;
        const theme = branch.themes?.[parsed.themeIdx];
        return !!theme && theme.isDeleted !== true;
      })
      .map((e) => {
        const store = storeMap.get(e.storeId);
        // 75차: PICK 카드를 좀 더 직관적으로(썸네일/장르/가격) 보여달라는 요청 —
        // store를 이미 통째로 읽어둔 김에 productId로 해당 테마를 그대로 찾아 붙임
        // (추가 Firestore 조회 없이, parseProductId는 storeId에 밑줄이 섞여도 안전)
        let themeInfo = null;
        let branchName = '';
        const parsed = e.productId ? parseProductId(e.productId) : null;
        if (parsed) {
          const branch = (store.branches || [])[parsed.branchIdx];
          branchName = branch?.branchName || '';
          const theme  = branch?.themes?.[parsed.themeIdx];
          if (theme) {
            const minPrice = theme.pricing?.length > 0
              ? Math.min(...theme.pricing.map(p => Number(p.price) || 0))
              : (Number(theme.price) || null);
            themeInfo = {
              imageUrl: theme.imageUrl || null,
              genre: theme.genre || '',
              difficulty: theme.difficulty || '',
              minPrice,
              // 80차 후속: 체험형(정원제) PICK 카드 배지/1인당 가격/좌석형태 표시용
              bookingFormat: theme.bookingFormat || 'room',
              pricePerPerson: Number(theme.pricePerPerson) || 0,
              seatingType: theme.seatingType || 'shared',
              isAdultOnly: !!theme.isAdultOnly,
            };
          }
        }
        // 78차: 매장명만으로는 지점 구분이 안 돼("솔버") 지점명까지 붙임("솔버 건대1호점")
        return {
          ...e,
          storeName: [getStoreDisplayName(store, '매장'), branchName].filter(Boolean).join(' '),
          ...(themeInfo || {}),
        };
      });
  } catch {
    return [];
  }
};

// 78차 43차: "오늘 기준 활성" 개수 조회 2종(getActivePaidPickSlotCount/
// getAllActivePaidPickCounts)이 BillingTab 월 선택(최근 6개월)/StoreAdminScreen
// 인보이스(최근 12개월)의 과거 달 조회에도 그대로 쓰이며, 그 달과 겹치지 않는 PICK까지
// 세거나 실제로 그 달에 과금됐던 PICK을 놓칠 수 있는 버그였음 — 특히 BillingTab의
// handleMarkPaid가 이 값으로 billings 문서를 덮어써서 과거 정산 기록을 오염시킬 수
// 있었음. scheduledMonthlySettlement(functions/index.js의 getPaidPickSlotCountServer)와
// 동일하게 month('YYYY-MM')와 실제 겹치는 기간으로 판정하는 버전으로 교체, 기존 2종은
// 호출부 없음 확인 후 삭제.
const overlapsMonth = (e, month) => {
  if (e.isActive === false || e.isPaidSlot !== true) return false;
  if (!e.startDate || !e.endDate) return false;
  const [y, m] = month.split('-').map(Number);
  const monthStart = `${month}-01`;
  const monthEnd = `${month}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0')}`;
  return e.startDate <= monthEnd && e.endDate >= monthStart;
};

// 79차: [버그 수정] branchIdx 파라미터 신규 — 이 함수가 항상 storeId 전체 기준으로만
// 세고 있어서, StoreAdminScreen.js가 특정 지점(scopeBranchIdx)만 골라 볼 때도 다른
// 지점의 PICK 추가요금까지 그대로 합산해 보여주고 있었음(fork로 발견, 지점별 매출/
// 테마개수/수수료/구독료는 78차62차에 이미 scopeBranchIdx로 스코프됐는데 PICK
// 추가요금만 그 작업에서 빠졌던 사각지대). 실제 매월 자동 청구서는 매장 단위 1건이라
// 영향 없음 — 매장관리자가 화면에서 지점을 골라볼 때의 표시/PDF 인보이스만 부정확했음.
// branchIdx가 null(매장 전체 보기)이면 기존과 동일하게 전체 합산.
export const getPaidPickSlotCountForMonth = async (storeId, month, branchIdx = null) => {
  if (!storeId) return 0;
  const snap = await getDocs(query(collection(db, 'events'), where('storeId', '==', storeId)));
  return snap.docs.filter((d) => {
    const e = d.data();
    if (!overlapsMonth(e, month)) return false;
    if (branchIdx !== null && e.branchIdx !== branchIdx) return false;
    return true;
  }).length;
};

export const getAllPaidPickCountsForMonth = async (month) => {
  const snap = await getDocs(query(collection(db, 'events'), where('isPaidSlot', '==', true)));
  const counts = {};
  snap.docs.forEach((d) => {
    const e = d.data();
    if (e.storeId && overlapsMonth(e, month)) {
      counts[e.storeId] = (counts[e.storeId] || 0) + 1;
    }
  });
  return counts;
};

// ─── D-Day 계산 (웹과 동일) ──────────────────────────────────
export const getDday = (endDate) => {
  const t = today();
  const diff = Math.ceil((new Date(endDate) - new Date(t)) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'D-day';
  if (diff > 0) return `D-${diff}`;
  return '종료';
};

